import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  CopyIcon,
  EditIcon,
  EyeIcon,
  TrashIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ChecklistsListResult } from "@/modules/checklist/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { checklistsApi } from "../api/client";
import { checklistsKeys, useDeleteChecklist } from "../api/queries";

type ChecklistRow = ChecklistsListResult["data"][number];

type ChecklistsTableProps = {
  data: ChecklistRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (checklist: ChecklistRow) => void;
  onView?: (id: string) => void;
  onCopy?: (id: string) => void;
};

// Type is now displayed as typeName from the API

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  draft: "secondary",
  inactive: "destructive",
};

export function ChecklistsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onCopy,
}: ChecklistsTableProps) {
  const { has } = usePermissions();
  const deleteChecklist = useDeleteChecklist();
  const qc = useQueryClient();

  const deactivateChecklistMutation = useMutation({
    mutationFn: ({
      id,
      currentData,
    }: {
      id: string;
      currentData: ChecklistRow;
    }) =>
      checklistsApi.update(id, {
        title: currentData.title,
        description: currentData.description ?? undefined,
        typeId: currentData.typeId,
        departmentId: currentData.departmentId ?? undefined,
        status: "inactive",
        scheduleType: currentData.scheduleType,
        scheduleFrequency: currentData.scheduleFrequency ?? undefined,
        scheduleDayOfWeek: currentData.scheduleDayOfWeek ?? undefined,
        scheduleDayOfMonth: currentData.scheduleDayOfMonth ?? undefined,
        requireAllItems: currentData.requireAllItems,
        requirePhoto: currentData.requirePhoto,
        requireNotesOnFail: currentData.requireNotesOnFail,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: checklistsKeys.all });
    },
  });

  const deactivateChecklist = {
    run: (id: string, currentData: ChecklistRow) =>
      new Promise<void>((resolve, reject) => {
        deactivateChecklistMutation.mutate(
          { id, currentData },
          {
            onSuccess: () => resolve(),
            onError: (e) => reject(e),
          },
        );
      }),
  };

  const activateChecklistMutation = useMutation({
    mutationFn: ({
      id,
      currentData,
    }: {
      id: string;
      currentData: ChecklistRow;
    }) =>
      checklistsApi.update(id, {
        title: currentData.title,
        description: currentData.description ?? undefined,
        typeId: currentData.typeId,
        departmentId: currentData.departmentId ?? undefined,
        status: "active",
        scheduleType: currentData.scheduleType,
        scheduleFrequency: currentData.scheduleFrequency ?? undefined,
        scheduleDayOfWeek: currentData.scheduleDayOfWeek ?? undefined,
        scheduleDayOfMonth: currentData.scheduleDayOfMonth ?? undefined,
        requireAllItems: currentData.requireAllItems,
        requirePhoto: currentData.requirePhoto,
        requireNotesOnFail: currentData.requireNotesOnFail,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: checklistsKeys.all });
    },
  });

  const activateChecklist = {
    run: (id: string, currentData: ChecklistRow) =>
      new Promise<void>((resolve, reject) => {
        activateChecklistMutation.mutate(
          { id, currentData },
          {
            onSuccess: () => resolve(),
            onError: (e) => reject(e),
          },
        );
      }),
  };

  const columns: TanstackReactTable.ColumnDef<ChecklistRow>[] = [
    createSortableColumn<ChecklistRow>("title", "ຊື່ Checklist", { size: 200 }),
    {
      id: "typeName",
      header: "ປະເພດ",
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.typeName || "-"}</Badge>
      ),
      size: 120,
    },
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<ChecklistRow>("updatedAt", "ວັນທີ່ອັບເດດ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.updatedAt)}
        </span>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: ChecklistRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has(Permissions.checklists.read) && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        if (has(Permissions.checklists.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.checklists.create) && onCopy) {
          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: () => onCopy(row.original.id),
          });
        }

        if (
          has(Permissions.checklists.update) &&
          row.original.status !== "inactive"
        ) {
          actions.push({
            label: "ປິດການໃຊ້ງານ",
            variant: "destructive",
            icon: <Ban className="h-4 w-4" />,
            onClick: async () => {
              const checklist = row.original;
              const ok = await confirm({
                title: "ປິດການໃຊ້ງານ Checklist",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານ "${checklist.title}"? (ບໍ່ຈະລຶບຂໍ້ມູນ)`,
                actionText: "ປິດການໃຊ້ງານ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                toast.promise(
                  deactivateChecklist.run(checklist.id, checklist),
                  {
                    loading: "ກໍາລັງປິດການໃຊ້ງານ...",
                    success: "ປິດການໃຊ້ງານສໍາເລັດ",
                    error: "ປິດການໃຊ້ງານລົ້ມເຫຼວ",
                  },
                );
              }
            },
          });
        }

        if (
          has(Permissions.checklists.update) &&
          row.original.status === "inactive"
        ) {
          actions.push({
            label: "ເປີດການໃຊ້ງານ",
            icon: <CheckCircle2 className="h-4 w-4" />,
            onClick: async () => {
              const checklist = row.original;
              const ok = await confirm({
                title: "ເປີດການໃຊ້ງານ Checklist",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດການໃຊ້ງານ "${checklist.title}"?`,
                actionText: "ເປີດການໃຊ້ງານ",
                ActionProps: {
                  variant: "default",
                },
              });
              if (ok) {
                toast.promise(activateChecklist.run(checklist.id, checklist), {
                  loading: "ກໍາລັງເປີດການໃຊ້ງານ...",
                  success: "ເປີດການໃຊ້ງານສໍາເລັດ",
                  error: "ເປີດການໃຊ້ງານລົ້ມເຫຼວ",
                });
              }
            },
          });
        }

        if (has(Permissions.checklists.delete)) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const checklist = row.original;
              const ok = await confirm({
                title: "ລຶບ Checklist",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${checklist.title}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                toast.promise(deleteChecklist.run(checklist.id), {
                  loading: "ກໍາລັງລຶບ...",
                  success: "ລຶບສໍາເລັດ",
                  error: "ລຶບລົ້ມເຫຼວ",
                });
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="checklist-table">
      <DataTable<ChecklistRow, unknown>
        noDataMessage="ບໍ່ພົບ Checklist"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
